import { classNames, Component, h, tag } from "omi";

import { NWProjectStatusIndicator } from "@/wc/components/nw-nav/components/projectStatusIndicator.tsx";
import { NWAccordion, NwAccordionItem } from "@/wc/components/atoms/nw-accordion";
import { getIconForCategory } from "@/wc/components/nw-nav/utils.ts";
import { Project, ProjectStatus } from "@/api";
import { IconName, NWTooltip } from "@/wc/components";
import { DaysPerWeek, HoursPerDay } from "@/models";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

import { useSchedule } from "../../store/queries/schedule.ts";
import { NavSchedule } from "../../components/navSchedule.tsx";
import { NavSection } from "../../components/navSection.tsx";
import { NavTimeSelector } from "../../components/navTimeSelector";
import { NavTopicList } from "../../components/navTopicList";
import { isCourse } from "../../store/models.ts";
import { NavStore } from "../../store";

type Props = {};

@tag("nav-course-track")
export class NavCourseTrack extends Component<Props> {
  static css = [tailwind];

  private schedule = useSchedule();

  install() {
    const hasSelectedTopics = NavStore.selectedTopics.value.length !== 0;
    this.schedule.fetch({ topics: hasSelectedTopics ? NavStore.getSelectedTopicsAsCategories() : undefined });
  }

  private isActiveProject(projectId: Project["id"]) {
    return projectId ? projectId === Store.project.id.value : false;
  }

  private getStorageKey(category: string): string {
    return category.replace(/ /g, "").toLowerCase();
  }

  private hasSchedule() {
    return (
      Store.user.preferences.hoursPerDay.value !== HoursPerDay.NONE &&
      Store.user.preferences.daysPerWeek.value !== DaysPerWeek.NONE
    );
  }

  private renderByTopic() {
    const projectsByTopic = NavStore.getProjectsByTopic();

    return (
      <>
        {projectsByTopic.map(([topic, projects]) => (
          <div key={topic.id}>
            <NWAccordion
              storageKey={this.getStorageKey(topic.id)}
              type="uncontrolled"
              label={isCourse(topic) ? topic.value.metadata?.title || "Topic has no title" : topic.value}
              icon={isCourse(topic) ? (topic.value.metadata!.icon! as IconName) : getIconForCategory(topic.value)}
              openByDefault={NavStore.selectedTopics.value.length === 1 && !NavStore.allSelected.value}
            >
              {projects.map(p => (
                <NwAccordionItem
                  type="link"
                  label={p.metadata!.title! || "Project has no title!"}
                  isActive={this.isActiveProject(p.id)}
                  withActiveIndicator={false}
                  href={`/projects/${p.id}`}
                >
                  <NWProjectStatusIndicator
                    isActive={this.isActiveProject(p.id)}
                    status={p.status || ProjectStatus.PROJECT_INCOMPLETE}
                  />
                </NwAccordionItem>
              ))}
            </NWAccordion>
          </div>
        ))}
      </>
    );
  }

  render() {
    const hasSchedule = this.hasSchedule();

    return (
      <div class="text-secondary h-full animate-slideInRightFast">
        <>
          <div class="border-b mb-3">
            <NavTopicList />

            {Store.features.hasFeature("projects.course_track") && (
              <div class="space-y-2 mt-3 pb-5">
                <div class="flex items-center space-x-2 relative">
                  <NWTooltip
                    placement="top"
                    content="How much time do you want to spend learning?"
                    maxWidth="360px"
                    delay={[250, 0]}
                  >
                    <h5 class="text-sm font-semibold uppercase">Study schedule</h5>
                  </NWTooltip>
                </div>
                <div>
                  <NavTimeSelector />
                </div>
              </div>
            )}
          </div>

          <NavSection>
            {Store.features.hasFeature("projects.course_track") && hasSchedule ? <NavSchedule /> : this.renderByTopic()}

            <div
              id="white-fade"
              style="background: linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, #FFF 100%)"
              class={classNames(
                "sticky -bottom-1 h-24 z-[51] w-full pointer-events-none transition-opacity duration-150",
              )}
            ></div>
          </NavSection>
        </>
      </div>
    );
  }
}
